import { Stages } from "../consts";
import { hasScheduledPayment } from "../paymentSchedule.utils";
import type {
  PriceFacts,
  PriceView,
  WireBooking,
  WireContractCalculations,
  WireListing,
  WireOfferQuote,
  WirePaymentQuote,
} from "../types";

import {
  getQuoteChildren,
  getQuoteMaster,
  getQuoteSpaceRentalChild,
} from "./contracts.utils";
import { createEmptyPriceFacts } from "./createEmptyPriceFacts";
import { mapQuoteItemChildrenToPriceAddOns } from "./mapAddOns";
import { mapCalculationsToPriceFacts } from "./mapCalculationsToPriceFacts";

type FromQuoteParams = {
  quote: WireOfferQuote | null | undefined;
  listing: WireListing | null | undefined;
  view: PriceView;
  credits?: number;
  disableSalesTax?: boolean;
  isOfferFromInquiry?: boolean;
  paymentQuote?: WirePaymentQuote | null;
  /** Optional booking start (ISO). Defaults to space-rental child `dts_start`. */
  bookingStart?: string | null;
};

/**
 * Reads offer/inquiry/checkout quote wire into flat `PriceFacts`.
 * Offer-stage quotes still include `update_difference` on the wire; suppress
 * `additionalPaymentsTotal` there until BE omits it for non-guest-update flows.
 */
const fromQuote = ({
  bookingStart = null,
  credits = 0,
  disableSalesTax = false,
  isOfferFromInquiry = false,
  listing,
  paymentQuote = null,
  quote,
  view,
}: FromQuoteParams): PriceFacts => {
  if (!quote) {
    return createEmptyPriceFacts({
      isDiy: listing?.full_service?.active !== true,
      roomType: listing?.full_service?.room_type ?? null,
    });
  }

  const master = getQuoteMaster(quote);
  const calculations = master?.calculations as
    | WireContractCalculations
    | undefined;
  const spaceRentalChild = getQuoteSpaceRentalChild(quote);
  const spaceRentalBooking =
    spaceRentalChild?.type === "SPACE_RENTAL"
      ? (spaceRentalChild.meta?.booking as WireBooking | undefined)
      : undefined;
  const durationHours = spaceRentalBooking?.duration;
  const eventEnd =
    spaceRentalChild?.type === "SPACE_RENTAL"
      ? spaceRentalChild.meta?.event_end
      : undefined;

  const facts = mapCalculationsToPriceFacts({
    calculations,
    listing,
    spaceRentalBooking,
    durationHours,
    eventEnd: eventEnd ? String(eventEnd) : null,
    bookingStart,
    upcomingChargeDate: master?.split_payment_charge_date
      ? String(master.split_payment_charge_date)
      : null,
    credits,
    disableSalesTax,
    hasPaymentSchedule: hasScheduledPayment({
      payment_schedule_type: master?.payment_schedule_type,
      split_payment_checkout: master?.split_payment_checkout,
    }),
    hasCapturedPayments: view.stage === Stages.PostCheckout,
    paymentSettlement:
      paymentQuote == null
        ? null
        : {
            currentPaidTotal: paymentQuote.current_paid_total,
            dueNow: paymentQuote.due_now,
            isOfferFromInquiry,
            refund: paymentQuote.refund,
            scheduledChargeDate: paymentQuote.scheduled_charge_date
              ? String(paymentQuote.scheduled_charge_date)
              : null,
            scheduledPayment: paymentQuote.scheduled_payment,
          },
  });

  const addOns = mapQuoteItemChildrenToPriceAddOns({
    children: getQuoteChildren(quote),
    deletedItemIds: master?.["deleted-item-ids"],
    perspective: view.perspective,
  });

  if (view.stage === Stages.Offer) {
    return { ...facts, addOns, additionalPaymentsTotal: null };
  }

  return { ...facts, addOns };
};

export type { FromQuoteParams };
export { fromQuote };
